/*
  File: src/components/dashboard/CalendlyCalendar.jsx
  Purpose: Modern calendar component for displaying Calendly events in Padam Lint section
  Features:
  - Month/Day view toggle
  - Event display with Calendly styling
  - Responsive design
  - Click on date to see day events
  - Navigate between months
*/

import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faCalendarDay,
  faCalendar,
  faClock,
  faUser,
  faVideo,
  faPhone,
  faMapMarkerAlt,
  faTimes,
  faExternalLinkAlt,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { fetchCalendlyEvents } from '../../services/calendlyService';

// Get Calendly config from environment
const CALENDLY_URL = import.meta.env?.VITE_CALENDLY_URL || process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/contact-padam/padam-reunion';
const CALENDLY_PRIMARY_COLOR = import.meta.env?.VITE_CALENDLY_PRIMARY_COLOR || process.env.REACT_APP_CALENDLY_PRIMARY_COLOR || '8b5cf6';
const CALENDLY_TEXT_COLOR = import.meta.env?.VITE_CALENDLY_TEXT_COLOR || process.env.REACT_APP_CALENDLY_TEXT_COLOR || 'ffffff';
const CALENDLY_BG_COLOR = import.meta.env?.VITE_CALENDLY_BG_COLOR || process.env.REACT_APP_CALENDLY_BG_COLOR || '0a0a0f';

// Event Badge Component
function EventBadge({ event, onClick }) {
  const startTime = new Date(event.start_time);
  const timeString = startTime.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });

  const eventIcon = event.event_type === 'phone' ? faPhone : faVideo;

  return (
    <button
      onClick={onClick}
      className="w-full text-left px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 hover:scale-105 group"
      style={{ 
        backgroundColor: `#${CALENDLY_PRIMARY_COLOR}20`,
        borderLeft: `3px solid #${CALENDLY_PRIMARY_COLOR}`
      }}
    >
      <div className="flex items-center gap-1.5">
        <FontAwesomeIcon 
          icon={eventIcon} 
          className="text-[10px]" 
          style={{ color: `#${CALENDLY_PRIMARY_COLOR}` }}
        />
        <span className="text-white/90 truncate">{timeString}</span>
      </div>
      <div className="text-white/70 truncate text-[10px] mt-0.5">{event.invitee_name}</div>
    </button>
  );
}

// Event Details Modal
function EventDetailsModal({ event, onClose }) {
  if (!event) return null;

  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);
  const dateString = startTime.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const timeString = `${startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="relative rounded-2xl border border-white/20 bg-gradient-to-br from-gray-900 to-gray-800 p-6 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} className="text-white/70" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div 
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-3"
            style={{ 
              backgroundColor: `#${CALENDLY_PRIMARY_COLOR}30`,
              color: `#${CALENDLY_TEXT_COLOR}`
            }}
          >
            <FontAwesomeIcon icon={event.event_type === 'phone' ? faPhone : faVideo} />
            {event.event_type === 'phone' ? 'Phone Call' : 'Video Meeting'}
          </div>
          <h3 className="text-xl font-bold text-white mb-2">{event.name}</h3>
        </div>

        {/* Event Details */}
        <div className="space-y-4">
          {/* Date & Time */}
          <div className="flex items-start gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `#${CALENDLY_PRIMARY_COLOR}20` }}
            >
              <FontAwesomeIcon icon={faClock} style={{ color: `#${CALENDLY_PRIMARY_COLOR}` }} />
            </div>
            <div>
              <div className="text-sm font-medium text-white">{dateString}</div>
              <div className="text-sm text-white/60">{timeString}</div>
            </div>
          </div>

          {/* Invitee */}
          <div className="flex items-start gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `#${CALENDLY_PRIMARY_COLOR}20` }}
            >
              <FontAwesomeIcon icon={faUser} style={{ color: `#${CALENDLY_PRIMARY_COLOR}` }} />
            </div>
            <div>
              <div className="text-sm font-medium text-white">{event.invitee_name}</div>
              <div className="text-sm text-white/60">{event.invitee_email}</div>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-start gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `#${CALENDLY_PRIMARY_COLOR}20` }}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: `#${CALENDLY_PRIMARY_COLOR}` }} />
            </div>
            <div>
              <div className="text-sm font-medium text-white">{event.location}</div>
              <div className="text-sm text-white/60">Meeting link will be provided</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 pt-6 border-t border-white/10 flex gap-3">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-4 py-2.5 rounded-lg font-medium text-sm text-white transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2"
            style={{ backgroundColor: `#${CALENDLY_PRIMARY_COLOR}` }}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
            View in Calendly
          </a>
        </div>
      </div>
    </div>
  );
}

// Day View Component
function DayView({ date, events, onBack }) {
  const dayEvents = events.filter(event => {
    const eventDate = new Date(event.start_time);
    return eventDate.toDateString() === date.toDateString();
  }).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const [selectedEvent, setSelectedEvent] = useState(null);

  const dateString = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all duration-200"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <span className="text-sm font-medium">Back to Calendar</span>
          </button>
          <div className="text-lg font-bold text-white">{dateString}</div>
        </div>

        {/* Events List */}
        <div className="space-y-3">
          {dayEvents.length === 0 ? (
            <div className="text-center py-12 rounded-xl bg-white/5 border border-white/10">
              <FontAwesomeIcon icon={faCalendarDay} className="text-4xl text-white/30 mb-3" />
              <p className="text-white/60">No events scheduled for this day</p>
            </div>
          ) : (
            dayEvents.map(event => {
              const startTime = new Date(event.start_time);
              const endTime = new Date(event.end_time);
              const timeString = `${startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;

              return (
                <button
                  key={event.id}
                  onClick={() => setSelectedEvent(event)}
                  className="w-full text-left p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `#${CALENDLY_PRIMARY_COLOR}30` }}
                        >
                          <FontAwesomeIcon 
                            icon={event.event_type === 'phone' ? faPhone : faVideo} 
                            style={{ color: `#${CALENDLY_PRIMARY_COLOR}` }}
                            className="text-sm"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-white truncate">{event.name}</h4>
                          <p className="text-xs text-white/60">{timeString}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-white/70">
                        <div className="flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faUser} className="text-[10px]" />
                          <span>{event.invitee_name}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="text-[10px]" />
                          <span>{event.location}</span>
                        </div>
                      </div>
                    </div>
                    <FontAwesomeIcon 
                      icon={faExternalLinkAlt} 
                      className="text-white/40 group-hover:text-white/70 transition-colors"
                    />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {selectedEvent && (
        <EventDetailsModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </>
  );
}

// Main Calendar Component
export default function CalendlyCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch events from Calendly API
  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        // Get events for current month
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const minStartTime = new Date(year, month, 1);
        const maxStartTime = new Date(year, month + 1, 0, 23, 59, 59);

        const fetchedEvents = await fetchCalendlyEvents({
          minStartTime,
          maxStartTime
        });
        
        setEvents(fetchedEvents);
      } catch (error) {
        console.error('Error loading Calendly events:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [currentDate]);

  // Calendar calculations
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Navigation
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  }, [year, month, daysInMonth, startingDayOfWeek]);

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today = new Date();

  // If a date is selected, show day view
  if (selectedDate) {
    return <DayView date={selectedDate} events={events} onBack={() => setSelectedDate(null)} />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-white/30 mb-3 animate-spin" />
          <p className="text-white/60">Loading calendar events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-white">{monthName}</h3>
          <button
            onClick={goToToday}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all duration-200"
          >
            Today
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={goToPreviousMonth}
            className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all duration-200"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            onClick={goToNextMonth}
            className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all duration-200"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b border-white/10 bg-white/5">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-3 text-center text-xs font-semibold text-white/60 uppercase tracking-wider">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="aspect-square border-r border-b border-white/5 bg-white/[0.02]" />;
            }

            const dayEvents = getEventsForDate(date);
            const isToday = date.toDateString() === today.toDateString();
            const isPast = date < today && !isToday;
            const hasEvents = dayEvents.length > 0;

            return (
              <button
                key={date.toISOString()}
                onClick={() => setSelectedDate(date)}
                className={`aspect-square border-r border-b border-white/5 p-2 transition-all duration-200 hover:bg-white/10 group ${
                  isPast ? 'bg-white/[0.02]' : 'bg-transparent'
                }`}
              >
                <div className="h-full flex flex-col">
                  {/* Day Number */}
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-sm font-medium ${
                        isToday
                          ? 'w-6 h-6 rounded-full flex items-center justify-center text-white font-bold'
                          : isPast
                          ? 'text-white/30'
                          : 'text-white/70 group-hover:text-white'
                      }`}
                      style={isToday ? { backgroundColor: `#${CALENDLY_PRIMARY_COLOR}` } : {}}
                    >
                      {date.getDate()}
                    </span>
                    {hasEvents && (
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: `#${CALENDLY_PRIMARY_COLOR}` }}
                      />
                    )}
                  </div>

                  {/* Event Badges (max 2 visible) */}
                  <div className="flex-1 space-y-1 overflow-hidden">
                    {dayEvents.slice(0, 2).map(event => (
                      <EventBadge key={event.id} event={event} onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDate(date);
                      }} />
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-[10px] text-white/50 text-center">
                        +{dayEvents.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Events Summary */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white">Upcoming Events</h4>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium hover:underline flex items-center gap-1.5 transition-colors"
            style={{ color: `#${CALENDLY_PRIMARY_COLOR}` }}
          >
            <span>Schedule New</span>
            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
          </a>
        </div>
        <div className="text-2xl font-bold text-white mb-1">{events.length}</div>
        <div className="text-sm text-white/60">Total scheduled meetings</div>
      </div>
    </div>
  );
}
